import { useEffect, useMemo, useRef, useState } from "react";
import { SidebarSlotContext } from "./lib/sidebarSlot";
import { LogOut } from "lucide-react";
import { QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { CommunitiesView, TeamsView } from "./views/AccessControl";
import { AssessmentsView } from "./views/AssessmentsView";
import { AssessmentEditView } from "./views/AssessmentEditView";
import { AssessmentReadOnlyView } from "./views/AssessmentReadOnlyView";
import { DialpadView } from "./views/DialpadView";
import { DataView } from "./views/DataView";
import { TeamsDashboardView } from "./views/TeamsDashboardView";
import { AgentsView } from "./views/AgentsView";
import { AgentProfileView } from "./views/AgentProfileView";
import { CallDetailView } from "./views/CallDetailView";
import { ManageUsersView } from "./views/UsersView";
import { ManageQuestionsView } from "./views/ManageQuestionsView";
import { HowToUseView } from "./views/HowToUseView";
import { SearchView } from "./views/SearchView";
import { RoundtableView } from "./views/RoundtableView";
import { GenerateScenariosView } from "./views/GenerateScenariosView";
import { useAuth } from "./auth.helpers";

import {
  api,
  type ApiError,
  type RangeMode,
  type TimeRange,
  type UserOut,
} from "@/lib/api";
import { makeQueryClient } from "@/lib/queryClient";
import { type NavKey, buildUrl, decodeAgentFrom, parseUrl } from "./lib/router";
import { visibleNavItems } from "./lib/navItems";
import { useDocumentTitle } from "./lib/useDocumentTitle";

// Static browser-tab section labels for the top-level views, matching
// the sidebar labels in navItems.tsx exactly. Detail routes
// (agent-profile, call-detail) own their own title — they set it from
// the loaded entity once data arrives — so they're intentionally
// absent here and `sectionTitleFor` returns `undefined` for them,
// which tells `useDocumentTitle` to leave the title alone.
// `assessment-edit` is a static sub-view of Assessments with no
// entity-driven title of its own, so it reuses the section label.
const SECTION_TITLES: Partial<Record<NavKey, string>> = {
  intelligence: "Teams",
  agents: "Agent",
  search: "Search",
  roundtable: "Roundtable",
  "generate-scenarios": "Generate Sales Scenarios",
  assessments: "Assessments",
  "assessment-edit": "Assessments",
  "assessment-view": "Assessments",
  dialpad: "Dialpad",
  data: "Data",
  users: "Manage Users",
  "manage-questions": "Manage Questions",
  teams: "Manage Teams",
  communities: "Manage Communities",
  "how-to-use": "How to Use",
};

function sectionTitleFor(nav: NavKey): string | undefined {
  return SECTION_TITLES[nav];
}

const queryClient = makeQueryClient();

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  not_provisioned:
    "Your account isn't set up in ConvoAI yet. Ask an administrator to add you.",
  tenant_not_allowed: "Your organization isn't permitted to sign in.",
  account_disabled: "Your account is disabled. Contact an administrator.",
  email_taken: "This email is already linked to a different Microsoft account.",
  invalid_state: "Sign-in expired or was tampered with. Please try again.",
  invalid_token: "Microsoft sign-in failed. Please try again.",
};

function SignInPage() {
  const { login } = useAuth();
  const params =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const errorCode = params.get("error");
  const errorMsg = errorCode
    ? AUTH_ERROR_MESSAGES[errorCode] ?? `Sign-in failed (${errorCode}).`
    : null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="text-center mb-8">
        <img src="/convoai-logo.svg" alt="ConvoAI" className="mx-auto h-12 w-auto" />
        <p className="text-sm text-muted-foreground mt-2">Sign in to continue.</p>
      </div>
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-6 shadow-md space-y-4">
        {errorMsg && (
          <div className="rounded border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {errorMsg}
          </div>
        )}
        <button
          type="button"
          onClick={() => login()}
          className="w-full rounded bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
        >
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}

function Sidebar({
  active,
  onSelect,
  me,
  collapsed,
  onToggleCollapsed,
  slotRef,
}: {
  active: NavKey;
  onSelect: (k: NavKey) => void;
  me: UserOut | null;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  slotRef: (el: HTMLDivElement | null) => void;
}) {
  const topItems = me ? visibleNavItems(me, "top") : [];
  const bottomItems = me ? visibleNavItems(me, "bottom") : [];
  type SidebarItem = (typeof topItems)[number];
  const renderItem = (i: SidebarItem) => {
    const isActive = active === i.key;
    return (
      <button
        key={i.key}
        type="button"
        data-nav-key={i.key}
        onClick={() => onSelect(i.key)}
        title={collapsed ? i.label : undefined}
        aria-label={i.label}
        className={`w-full rounded text-sm flex items-center transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
          collapsed ? "justify-center h-9 px-0" : "px-3 py-2 text-left"
        } ${
          isActive
            ? "bg-sidebar-primary/10 text-sidebar-foreground border-l-2 border-sidebar-primary"
            : "text-sidebar-foreground/65 hover:bg-sidebar-primary/10 hover:text-sidebar-foreground"
        }`}
      >
        {collapsed ? (
          i.icon
        ) : (
          <span className="flex items-center gap-2">
            {i.icon}
            {i.label}
          </span>
        )}
      </button>
    );
  };
  return (
    <aside
      className={`shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground flex flex-col transition-[width] duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
        collapsed ? "w-12" : "w-56"
      }`}
    >
      <div className="p-2 border-b border-sidebar-border/40">
        <button
          type="button"
          onClick={onToggleCollapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`w-full rounded text-sidebar-foreground/65 hover:bg-sidebar-primary/10 hover:text-sidebar-foreground flex items-center transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
            collapsed ? "justify-center h-8" : "justify-end px-2 py-1.5"
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className={`h-4 w-4 transition-transform duration-[var(--duration-base)] ease-[var(--easing-standard)] ${
              collapsed ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M12.78 5.22a.75.75 0 0 1 0 1.06L9.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
      <nav className="p-2">
        <div className="space-y-1">
          {topItems.map(renderItem)}
        </div>
        <hr className="my-2 border-t border-sidebar-border/40" />
        <div className="space-y-1">
          {bottomItems.map(renderItem)}
        </div>
      </nav>
      {!collapsed ? (
        <div ref={slotRef} className="flex-1 px-2 pb-4 pt-2" />
      ) : null}
    </aside>
  );
}

function Shell() {
  const { isAuthenticated, user: authUser, logout } = useAuth();
  const initial = useMemo(() => parseUrl(), []);
  const [nav, setNavState] = useState<NavKey>(initial.nav);
  const [agentSlugState, setAgentSlugState] = useState<string | null>(
    initial.agentSlug,
  );
  const [agentKeyState, setAgentKeyState] = useState<string | null>(
    initial.agentKey,
  );
  const [sliceState, setSliceState] = useState<string | null>(initial.slice);
  const [teamParamState, setTeamParamState] = useState<string | null>(
    initial.team,
  );
  const [callIdState, setCallIdState] = useState<string | null>(initial.callId);
  const [fromState, setFromState] = useState<string | null>(initial.from);
  // Ticket 12/13: range pre-selection URL state. Stored as raw strings;
  // the narrow-to-valid step happens at the mount site so a stray URL
  // never blows up the agent view.
  const [timeRangeState, setTimeRangeState] = useState<TimeRange | null>(
    initial.timeRange,
  );
  const [rangeModeState, setRangeModeState] = useState<RangeMode | null>(
    initial.rangeMode,
  );
  // Task #365 — paired custom window (YYYY-MM-DD UTC) that round-trips
  // through the URL alongside ``timeRange``. Only meaningful when the
  // active range is "custom"; threaded into AgentProfileView and used
  // when re-building the URL on navigate().
  const [customStartState, setCustomStartState] = useState<string | null>(
    initial.customStart,
  );
  const [customEndState, setCustomEndState] = useState<string | null>(
    initial.customEnd,
  );
  // Task #290 — focused measure deep-link state (mid/ln URL params).
  // Only meaningful on agent-profile nav; ignored everywhere else.
  const [focusedMeasureIdState, setFocusedMeasureIdState] = useState<
    string | null
  >(initial.focusedMeasureId);
  const [focusedLabelNameState, setFocusedLabelNameState] = useState<
    string | null
  >(initial.focusedLabelName);

  const navigate = (
    next: NavKey,
    slug: string | null = null,
    key: string | null = null,
    opts?: {
      slice?: string | null;
      team?: string | null;
      callId?: string | null;
      from?: string | null;
      timeRange?: TimeRange | null;
      rangeMode?: RangeMode | null;
      // Task #365 — paired YYYY-MM-DD UTC bounds for the "custom"
      // preset. Both must be supplied together; otherwise the custom
      // window is dropped from the URL and the agent view falls back
      // to its default range.
      customStart?: string | null;
      customEnd?: string | null;
      focusedMeasureId?: string | null;
      focusedLabelName?: string | null;
    },
  ) => {
    const slice = opts?.slice ?? null;
    const team = opts?.team ?? null;
    const callId = opts?.callId ?? null;
    const from = opts?.from ?? null;
    const timeRange = opts?.timeRange ?? null;
    const rangeMode = opts?.rangeMode ?? null;
    const customStart = opts?.customStart ?? null;
    const customEnd = opts?.customEnd ?? null;
    const focusedMeasureId = opts?.focusedMeasureId ?? null;
    const focusedLabelName = opts?.focusedLabelName ?? null;
    setNavState(next);
    setAgentSlugState(slug);
    setAgentKeyState(key);
    setSliceState(slice);
    setTeamParamState(team);
    setCallIdState(callId);
    setFromState(from);
    setTimeRangeState(timeRange);
    setRangeModeState(rangeMode);
    setCustomStartState(customStart);
    setCustomEndState(customEnd);
    setFocusedMeasureIdState(focusedMeasureId);
    setFocusedLabelNameState(focusedLabelName);
    const url = buildUrl(next, slug, key, {
      slice,
      team,
      callId,
      from,
      timeRange,
      rangeMode,
      customStart,
      customEnd,
      focusedMeasureId,
      focusedLabelName,
    });
    if (
      typeof window !== "undefined" &&
      window.location.pathname + window.location.search !== url
    ) {
      window.history.pushState(
        {
          nav: next,
          agentSlug: slug,
          agentKey: key,
          slice,
          team,
          callId,
          from,
          timeRange,
          rangeMode,
          customStart,
          customEnd,
          focusedMeasureId,
          focusedLabelName,
        },
        "",
        url,
      );
    }
  };

  // T12 — the page formerly served at ``/clients`` now lives at
  // ``/search``. Rewrite the address bar once on load so existing
  // bookmarks/links to ``/clients`` land on ``/search`` (query string
  // preserved). ``parseUrl`` already maps the legacy segment to the
  // Search view, so the page renders correctly regardless.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    if (/\/clients(?=\/|$)/.test(path)) {
      const next =
        path.replace(/\/clients(?=\/|$)/, "/search") + window.location.search;
      window.history.replaceState({ nav: "search" }, "", next);
    }
  }, []);

  // Keep state in sync with browser back/forward.
  useEffect(() => {
    const onPop = () => {
      const parsed = parseUrl();
      setNavState(parsed.nav);
      setAgentSlugState(parsed.agentSlug);
      setAgentKeyState(parsed.agentKey);
      setSliceState(parsed.slice);
      setTeamParamState(parsed.team);
      setCallIdState(parsed.callId);
      setFromState(parsed.from);
      setTimeRangeState(parsed.timeRange);
      setRangeModeState(parsed.rangeMode);
      setCustomStartState(parsed.customStart);
      setCustomEndState(parsed.customEnd);
      setFocusedMeasureIdState(parsed.focusedMeasureId);
      setFocusedLabelNameState(parsed.focusedLabelName);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const setNav = (k: NavKey) => navigate(k, null, null);
  const [sidebarSlot, setSidebarSlot] = useState<HTMLDivElement | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem("sidebarCollapsed", String(sidebarCollapsed));
    } catch {
      // ignore (e.g. private mode quota)
    }
  }, [sidebarCollapsed]);
  const qc = useQueryClient();

  // If the session transitions from signed-in → signed-out (e.g. a refresh
  // failed while still signed in), drop cached protected data so a stale `me`
  // can't keep rendering the dashboard behind a gone session.
  const prevAuthed = useRef(false);
  useEffect(() => {
    if (prevAuthed.current && !isAuthenticated) {
      qc.clear();
    }
    prevAuthed.current = isAuthenticated;
  }, [isAuthenticated, qc]);

  const meQ = useQuery<UserOut>({
    queryKey: ["me"],
    queryFn: () => api.me(),
    enabled: isAuthenticated,
  });

  const me = meQ.data;

  // Only a real authorization failure (401/403) is "access denied". A network
  // failure (status 0, set by the api client) or a server error is transient —
  // the query keeps polling (refetchInterval), so it recovers on its own and
  // must NOT masquerade as a permissions problem.
  const meErr = meQ.error as Partial<ApiError> | null;
  const meAccessDenied = meErr?.status === 401 || meErr?.status === 403;

  // Guard: route the user back to the Teams view if they try to deep-link a
  // sidebar page they aren't allowed to see. Drives off the same registry
  // the sidebar itself reads from, so visibility rules stay in one place.
  // Sub-views (agent-profile, call-detail) aren't sidebar entries; they
  // follow the same permission rules as their parent ("agents") which is
  // visible to everyone.
  const effectiveNav: NavKey = useMemo(() => {
    if (!me) return nav;
    if (nav === "agent-profile" || nav === "call-detail" || nav === "agents")
      return nav;
    if (nav === "intelligence") return nav;
    // `assessment-edit` is a sub-view of the supadmin-only Assessments
    // page. Gate it on the same visibility rule as the parent so
    // deep-links from non-supadmins still bounce to the Teams view.
    if (nav === "assessment-edit") {
      return me.role === "supadmin" ? nav : "intelligence";
    }
    // T2 — read-only assessment detail is reachable by anyone who can
    // see the Assessments page (supadmins, admins and managers). Access
    // to the specific assessment is still enforced server-side by the
    // scoped endpoint, which 404s anything outside the actor's scope.
    if (nav === "assessment-view") {
      const canSeeAssessments = visibleNavItems(me).some(
        (i) => i.key === "assessments",
      );
      return canSeeAssessments ? nav : "intelligence";
    }
    const allowed = visibleNavItems(me).some((i) => i.key === nav);
    return allowed ? nav : "intelligence";
  }, [nav, me]);

  // Browser-tab title for the active section. Detail routes resolve to
  // `undefined` here and set their own title from the loaded entity.
  useDocumentTitle(sectionTitleFor(effectiveNav));

  return (
    <SidebarSlotContext.Provider value={sidebarSlot}>
    <div className="min-h-screen bg-background">
      {/* 2px brand-green accent line along the very top edge */}
      <div className="h-0.5 w-full bg-primary" aria-hidden="true" />
      <header className="bg-card border-b border-border">
        <div className="flex items-center justify-between px-6 py-3">
          <img src="/convoai-logo.svg" alt="ConvoAI" className="h-8 w-auto" />
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{authUser?.email}</span>
            <button
              type="button"
              onClick={() => logout()}
              className="inline-flex items-center gap-1 rounded border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted/40 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>
      <div className="flex min-h-[calc(100vh-59px)]">
        <Sidebar
          active={
            effectiveNav === "agent-profile" || effectiveNav === "call-detail"
              ? "agents"
              : effectiveNav
          }
          onSelect={setNav}
          me={me ?? null}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
          slotRef={setSidebarSlot}
        />
        <main className="flex-1 p-6 max-w-6xl">
          {!isAuthenticated ? (
            <div className="text-muted-foreground">Authenticating…</div>
          ) : meQ.error && meAccessDenied ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
              <div className="font-medium">Access denied</div>
              <div className="text-sm mt-1">
                {meErr?.message || "You don't have access to this."}
              </div>
              <div className="text-xs mt-2 text-destructive/80">
                Signed in as {authUser?.email}
              </div>
            </div>
          ) : meQ.error ? (
            <div className="rounded-lg border border-border bg-muted/40 p-4 text-muted-foreground">
              <div className="font-medium text-foreground">Reconnecting…</div>
              <div className="text-sm mt-1">
                Couldn't reach the server. Retrying automatically.
              </div>
            </div>
          ) : !me ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : effectiveNav === "users" ? (
            <ManageUsersView me={me} />
          ) : effectiveNav === "manage-questions" ? (
            // PET-641 — deep-link guard is free: `manage-questions` is a
            // sidebar key, so the visibleNavItems fallback in
            // `effectiveNav` already bounces managers to Intelligence.
            <ManageQuestionsView />
          ) : effectiveNav === "communities" ? (
            <CommunitiesView />
          ) : effectiveNav === "teams" ? (
            <TeamsView me={me} />
          ) : effectiveNav === "assessments" ? (
            <AssessmentsView
              me={me}
              navigate={(nav, id) => navigate(nav, id, null)}
            />
          ) : effectiveNav === "assessment-edit" ? (
            <AssessmentEditView
              id={agentSlugState ?? "new"}
              onBack={() => navigate("assessments", null, null)}
              onCreated={(a) => navigate("assessment-edit", a.id, null)}
            />
          ) : effectiveNav === "assessment-view" && agentSlugState ? (
            <AssessmentReadOnlyView
              id={agentSlugState}
              onBack={() => navigate("assessments", null, null)}
            />
          ) : effectiveNav === "dialpad" ? (
            <DialpadView me={me} />
          ) : effectiveNav === "data" ? (
            // Wave 9 — `data` is a sidebar key, so the visibleNavItems
            // deep-link guard in `effectiveNav` already bounces admins
            // and managers to Intelligence.
            <DataView me={me} />
          ) : effectiveNav === "how-to-use" ? (
            <HowToUseView me={me} />
          ) : effectiveNav === "search" ? (
            <SearchView />
          ) : effectiveNav === "roundtable" ? (
            // Wave 5 · T1 — `roundtable` is a sidebar key, so the
            // visibleNavItems deep-link guard in `effectiveNav` covers
            // it for free (it is visible to all three roles anyway).
            <RoundtableView />
          ) : effectiveNav === "generate-scenarios" ? (
            // Wave 10 (port-plan 24) — `generate-scenarios` is a sidebar
            // key, so the visibleNavItems deep-link guard in
            // `effectiveNav` already bounces managers to Intelligence.
            <GenerateScenariosView />
          ) : effectiveNav === "intelligence" ? (
            <TeamsDashboardView
              me={me}
              onSelectAgent={(slug, key, opts) =>
                navigate("agent-profile", slug, key, opts)
              }
              onOpenCall={(callId) =>
                // Task #286 — the focused calls list under a team in
                // the Teams view "By Assessment" surface opens calls in
                // the same detail view. There's no agent profile to
                // return to, so omit ``from`` and the CallDetail header
                // falls back to "Back to agents" via the default route.
                navigate("call-detail", null, null, { callId })
              }
            />
          ) : effectiveNav === "agent-profile" && agentSlugState ? (
            <AgentProfileView
              slug={agentSlugState}
              agentKey={agentKeyState}
              slice={sliceState}
              teamId={teamParamState}
              initialTimeRange={timeRangeState}
              initialRangeMode={rangeModeState}
              initialCustomRange={
                customStartState && customEndState
                  ? { start: customStartState, end: customEndState }
                  : null
              }
              initialFocusedMeasure={
                focusedMeasureIdState && focusedLabelNameState
                  ? {
                      measure_id: focusedMeasureIdState,
                      label_name: focusedLabelNameState,
                    }
                  : null
              }
              me={me ?? null}
              onBack={() => navigate("agents", null, null)}
              onChangeSlice={(slice) =>
                navigate("agent-profile", agentSlugState, agentKeyState, {
                  slice,
                  team: teamParamState,
                  timeRange: timeRangeState,
                  rangeMode: rangeModeState,
                  customStart: customStartState,
                  customEnd: customEndState,
                })
              }
              onRangeChange={(tr, rm, cr) =>
                navigate("agent-profile", agentSlugState, agentKeyState, {
                  slice: sliceState,
                  team: teamParamState,
                  timeRange: tr,
                  rangeMode: rm,
                  customStart: cr?.start ?? null,
                  customEnd: cr?.end ?? null,
                })
              }
              onOpenCall={(callId, fromToken) =>
                navigate("call-detail", null, null, {
                  callId,
                  from: fromToken,
                })
              }
              onAssignAssessment={() =>
                navigate("assessments", null, null)
              }
            />
          ) : effectiveNav === "call-detail" && callIdState ? (
            <CallDetailView
              callId={callIdState}
              me={me ?? null}
              onBack={() => {
                const f = decodeAgentFrom(fromState);
                if (f) {
                  navigate("agent-profile", f.slug, f.agentKey, {
                    slice: f.slice,
                    team: f.team,
                  });
                } else {
                  navigate("agents", null, null);
                }
              }}
              backLabel={fromState ? "Back to agent profile" : "Back to agents"}
            />
          ) : (
            <AgentsView
              me={me}
            />
          )}
        </main>
      </div>
    </div>
    </SidebarSlotContext.Provider>
  );
}

function AuthCacheSync() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const prevUserId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = prevUserId.current;
    const current = user?.id ?? null;
    if (prev !== undefined && prev !== current) {
      qc.clear();
    }
    prevUserId.current = current;
  }, [user, qc]);
  return null;
}

function Root() {
  const { isAuthenticated, isHydrating } = useAuth();
  if (isHydrating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  // The Azure callback redirects back to /auth/callback?ok=1. By now the session
  // cookie is set and /auth/me has populated the user — clean the URL back to
  // the app root. On failure (?error=...) the user is unauthenticated and the
  // sign-in page reads the error code from the URL.
  if (
    typeof window !== "undefined" &&
    window.location.pathname === "/auth/callback" &&
    isAuthenticated
  ) {
    window.history.replaceState({}, "", "/");
  }
  return (
    <>
      <AuthCacheSync />
      {isAuthenticated ? <Shell /> : <SignInPage />}
    </>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  );
}
